/* eslint-disable camelcase */
/* eslint-disable no-console */
import { useRef, useEffect, useState } from 'react';

import { useRouter } from 'next/router';
import { useRecoilState, useSetRecoilState } from 'recoil';

import { ERROR_REPORT_SCOPE } from './constants';
import { isValidPatient } from './flight-path';
import { showToastMessage } from './flightpath/toast';
import { getLocalStorageItem, setLocalStorageItem } from './storage';

import { getUser, enableLineLogin, otpLoginWithLine } from '../apis/auth';
import { errorReport, fetchFeatureEnabledStatus, getAllTreatments, validateToken } from '../apis/flightPath';
import AuthRestrictLoader from '../containers/flight-path/authRestrictLoader';
import useI18n from '../hooks/use-i18n';
import { error, ERROR_TYPES } from '../recoil/atoms/flightPath';
import { session } from '../recoil/atoms/session';
import { allTreatments } from '../recoil/selectors/flightPath';

const authRestrict = (Component) => {
  const Auth = (props) => {
    const i18n = useI18n();
    const router = useRouter();
    const { token, show_wear_time_info, page_type } = router.query;
    const { isReady, asPath, pathname, query } = router;

    const tokenValidated = useRef(false);
    const sessionSettingFailed = useRef(false);

    const setTreatments = useSetRecoilState(allTreatments);
    const [userSession, setUserSession] = useRecoilState(session);
    const setError = useSetRecoilState(error);
    const [isLoading, setIsLoading] = useState(true);
    const [isNavigating, setIsNavigating] = useState(false);

    const retainersStandAlone = userSession?.user?.roles?.includes('CUSTOMER')
                                && userSession?.exception === 'TREATMENT_OVERVIEW';
    const flightpathSales = !retainersStandAlone
                                && (userSession?.user?.roles?.includes('CUSTOMER')
                                || userSession?.exception === 'SALES');

    // Setup router change event listeners for navigation state
    useEffect(() => {
      const handleRouteChangeStart = () => {
        setIsNavigating(true);
      };

      const handleRouteChangeComplete = () => {
        setIsNavigating(false);
      };

      const handleRouteChangeError = () => {
        setIsNavigating(false);
      };

      router.events.on('routeChangeStart', handleRouteChangeStart);
      router.events.on('routeChangeComplete', handleRouteChangeComplete);
      router.events.on('routeChangeError', handleRouteChangeError);

      return () => {
        router.events.off('routeChangeStart', handleRouteChangeStart);
        router.events.off('routeChangeComplete', handleRouteChangeComplete);
        router.events.off('routeChangeError', handleRouteChangeError);
      };
    }, [router]);

    // Fetch user details and feature flag
    async function fetchUserDetails() {
      try {
        const userRes = await getUser();
        if (userRes) {
          const feature = await fetchFeatureEnabledStatus();
          if (feature?.is_feature_enabled) {
            setUserSession((prev) => ({
              ...prev,
              session: true,
              loaded: true,
              user: userRes.data,
              login_type: userRes.session?.type || 'patient',
              error: false
            }));
            return true;
          }
          if (!asPath.includes('login')) {
            const notFoundPath = `/${i18n.activeLocale}/flight-path/404`;
            if (asPath !== notFoundPath) router.replace(notFoundPath);
            return false;
          }
        }
      } catch (err) {
        console.error('fetchUserDetails error', err);
      }
      if (asPath.includes('/flight-path/retainers')) {
        localStorage.setItem('endpoint', 'retainers');
      }
      sessionSettingFailed.current = true;
      setUserSession((prev) => ({ ...prev, session: false, loaded: false, error: true, user: {} }));
      return false;
    }

    // Fetch patient treatments
    async function fetchTreatmentDetails() {
      setIsLoading(true);
      try {
        if (userSession.user.roles.includes('PATIENT')) {
          const res = await getAllTreatments();
          if (res) {
            setTreatments(res);
            const { isValidUser, redirectToTreatmentPlan } = isValidPatient(res);
            setLocalStorageItem('isValidUser', isValidUser);
            setLocalStorageItem('redirectToTreatmentPlan', redirectToTreatmentPlan);
          }
        } else {
          if (retainersStandAlone) {
            setLocalStorageItem('isValidUser', true);
          } else {
            setLocalStorageItem('isValidUser', false);
          }
          setLocalStorageItem('redirectToTreatmentPlan', false);
        }
      } catch (err) {
        errorReport({
          scope: ERROR_REPORT_SCOPE.TREATMENT_FETCH,
          message: err.toString()
        });
      }
      setIsLoading(false);
    }

    const handleEnableLineLogin = async () => {
      try {
        const uid = localStorage.getItem('uid');
        const res = await enableLineLogin(uid, {
          authCode: query.code,
          locale: i18n.activeLocale
        });
        const url = new URL(window.location);
        url.searchParams.delete('code');
        url.searchParams.delete('profile');
        window.history.replaceState({}, document.title, url.toString());
        if (res.status === 409) {
          showToastMessage(
            'LineId could not be associated with your account,\n'
            + 'Please contact Zenyum Customer Care for assistance',
            false
          );
        }
        localStorage.removeItem('uid');
      } catch (err) {
        console.error('LINE login error:', err);
      }
    };

    const handleLineAuth = async () => {
      const response = await otpLoginWithLine({
        authCode: query.code,
        locale: i18n.activeLocale
      });
      const url = new URL(window.location);
      url.searchParams.delete('code');
      url.searchParams.delete('profile');
      window.history.replaceState({}, document.title, url.toString());
      if (response?.data && response?.data?.idToken) {
        handleUserSession();
      } else {
        showToastMessage(
          'Account could not be created because the line id is already registered ,\n'
          + 'If you already have a Zenyum account, please login with your existing credentials '
          + 'and associate your line id with your account',
          false
        );
        setError(ERROR_TYPES.LINE_AUTH);
        const loginPath = `/${i18n.activeLocale}/login`;
        router.replace(loginPath);
        return false;
      }
    };

    // Handle initial login or token flow
    async function handleUserSession() {
      if (token && !tokenValidated.current) {
        let valid = false;
        valid = await validateToken(token);
        if (!valid) {
          errorReport({
            scope: ERROR_REPORT_SCOPE.AUTH_TOKEN_VERIFY,
            message: 'Token validation failed',
            token
          });
        }

        const { token: _, ...rest } = query;
        router.replace({ pathname, query: rest }, undefined, { shallow: true });

        if (valid) {
          const res = await fetchUserDetails();
          if (res && !localStorage.getItem('isUserFirstLogin')) {
            localStorage.setItem('isUserFirstLogin', 'true');
          }
        } else {
          const loginPath = `/${i18n.activeLocale}/login`;
          if (asPath !== loginPath) router.replace(loginPath);
        }

        tokenValidated.current = true;
      } else if (!userSession.loaded && !userSession.loggedOut) {
        await fetchUserDetails();
      }
      setIsLoading(false);
    }

    useEffect(() => {
      if (isReady && !sessionSettingFailed.current) {
        handleUserSession();
      }
    }, [isReady, token]);

    useEffect(() => {
      const uid = localStorage.getItem('uid');
      if (!userSession?.user?.lineId) {
        if (uid && router.query.code) {
          handleEnableLineLogin();
        } else if (router.query.code && !uid) {
          handleLineAuth();
        }
      }
    }, [JSON.stringify(router.query)]);

    // Fetch treatments when session becomes active
    useEffect(() => {
      if (userSession.session && !flightpathSales) {
        fetchTreatmentDetails();
      }
      if (flightpathSales && isLoading) {
        setIsLoading(false);
      }
    }, [userSession.session, flightpathSales]);

    // Redirect logic
    useEffect(() => {
      if (isLoading || !isReady) return;

      const loginPath = `/${i18n.activeLocale}/login`;
      const notFoundPath = `/${i18n.activeLocale}/flight-path/404`;
      const treatmentPathBase = `/${i18n.activeLocale}/flight-path/treatment-overview`;
      const salesPath = `/${i18n.activeLocale}/flight-path/sales`;

      // Not logged in → login
      if (!userSession.session && !asPath.includes('/login') && !asPath.includes('/404')) {
        if (asPath !== loginPath) router.replace(loginPath);
      }

      // Login error → 404
      if (!asPath.includes('/login') && userSession.error) {
        if (asPath !== notFoundPath) router.replace(notFoundPath);
      }

      if (userSession.session) {
        const isValidUser = JSON.parse(getLocalStorageItem('isValidUser') || 'false');
        const redirectToTreatmentPlan = JSON.parse(
          getLocalStorageItem('redirectToTreatmentPlan') || 'false'
        );
        const endpoint = localStorage.getItem('endpoint');

        // Add Exception cases
        if (isValidUser && endpoint === 'retainers') {
          setIsNavigating(true);
          router.replace(`/${i18n.activeLocale}/flight-path/retainers`);
        }
        if (retainersStandAlone && !asPath.includes('/treatment-overview')) {
          setIsNavigating(true);
          router.replace(`/${i18n.activeLocale}/flight-path/treatment-overview`);
        }
        // Valid patient → treatment overview
        if (
          isValidUser
          && !flightpathSales
          && (asPath.includes('/login') || asPath.includes('/404') || asPath.includes('/sales'))
        ) {
          const params = [];
          if (show_wear_time_info === 'true') params.push('show_wear_time_info=true');
          if (page_type) params.push(`page_type=${page_type}`);
          const qs = params.length ? `?${params.join('&')}` : '';
          const target = treatmentPathBase + qs;
          if (asPath !== target) {
            setIsNavigating(true);
            const currentQuery = query;
            const targetUrl = new URL(target, window.location.origin);
            Object.keys(currentQuery).forEach((key) => {
              if (!targetUrl.searchParams.has(key)) {
                targetUrl.searchParams.set(key, currentQuery[key]);
              }
            });
            // Remove code and state parameters from URL
            targetUrl.searchParams.delete('code');
            targetUrl.searchParams.delete('state');
            router.replace(targetUrl.pathname + targetUrl.search);
          }
        }

        // Sales or redirect flag → sales page
        if (
          (flightpathSales || redirectToTreatmentPlan)
          && (asPath.includes('/login') || asPath.includes('/404')
          || asPath.includes('/treatment-overview')
          || asPath.includes('/retainers'))
        ) {
          if (asPath !== salesPath) {
            setIsNavigating(true);
            router.replace(salesPath);
          }
        }
      }
    }, [isLoading, isReady, userSession.session, userSession.error, asPath, flightpathSales, query?.code]);

    if (isLoading || !isReady || isNavigating || query?.code) {
      return <AuthRestrictLoader />;
    }
    return <Component {...props} />;
  };

  if (Component.getInitialProps) {
    Auth.getInitialProps = Component.getInitialProps;
  }

  return Auth;
};

export default authRestrict;
